<script>
  import { _ } from "svelte-i18n"
  import { onMount, createEventDispatcher } from 'svelte'
  import { 
    preview_mode, previews_nav_filters, previews_are_zoomed, previews_font_size, 
    visits, local_fonts, previews_last_scroll_position, previews_storefronts_filter,
    loaded_uploaded_fonts_counter, can_upgrade_from_pro_to_business, business_subscription_active
  } from './../stores'
  import PreviewText from './PreviewText.svelte'
  import PreviewButton from "./PreviewButton.svelte";
  import PreviewNameOrDownloadLink from './PreviewNameOrDownloadLink.svelte'
  import PreviewTags from './PreviewTags.svelte'
  import Button from '../elements/Button.svelte'
  import GlyphsViewer from "./GlyphsViewer.svelte";
  import { loadAndProcessUploadedFontFile } from '../services/font_files';

  const dispatch = createEventDispatcher()

  export let font

  let zoomed = false
  let hovering = false
  let fontinactive = false
  let preview_element
  let started_loading_font_file = false

  // Check if the font file should be loaded
  $: should_load_font_file = (
    // It should have a file name and be not loaded or loading
    (!!font.file_url && !font.font_file_loaded && !font.font_file_loading) && (
      // If it is a detected font, only load font file if we are in storefronts
      ((font.source === 'local' || font.source === 'dropped' || font.source === 'extension') && $previews_nav_filters.storefront) || 
      // If not a detected font, load font file as soon as mounted
      (font.source !== 'local' && font.source !== 'dropped' && font.source !== 'extension')
    ) &&
    // For uploaded fonts, check the counter limit
    ($business_subscription_active ? true : loaded_uploaded_fonts_counter.canLoad())
  )
    
  $: zoomed_but_not_me = $previews_are_zoomed && !zoomed
  $: if (should_load_font_file) loadUploadedFontFile()

  onMount(async () => {
    if (should_load_font_file) {
      await loadUploadedFontFile()
    }
  })

  async function loadUploadedFontFile() {
    // Only load once
    if (started_loading_font_file) return
    started_loading_font_file = true

    // set font object's loading flag
    font.font_file_loading = true
    dispatch('updateFont', font)
    
    // Increment counter for uploaded fonts before loading
    if (font.uploaded && $can_upgrade_from_pro_to_business) {
      loaded_uploaded_fonts_counter.increment()
    }
    
    // load font file
    await loadAndProcessUploadedFontFile(font.file_name, font.file_url, font.postscript_name, font.id);
    // font_file_loading, font_file_loaded and failed_to_load_font_file are set by loadAndProcessUploadedFontFile 
  }

  function handleClick() {
    console.log('Preview - clicked', font)
    // can't select if in storefront
    if ($previews_nav_filters.storefront) {
      return
    }
    // if selected fonts are filtered
    if ($previews_nav_filters.selected) {
      // zoom in if not already zoomed
      if (!zoomed) {
        toggleZoom()
      }  
      // exit
      return
    }
    // can't select if zoomed
    if ($previews_are_zoomed) { return }
    // select or deselect
    toggleSelect()
  }
  
  
  function toggleSelect() {
    font.selected = !font.selected
    dispatch('updateFont', font)
  }
  
  function select() {
    font.selected = true
    dispatch('updateFont', font)
  }

  function deselect() {
    font.selected = false
    dispatch('updateFont', font)
  }

  function removeFromStorefront() {
    // remove storefront with id from font.storefronts
    font.storefronts = font.storefronts.filter(s => s.id !== $previews_storefronts_filter)
    // if no storefronts left, set font.storefront to null
    if (font.storefront.length === 0) {
      font.storefront = null
    }
    dispatch('updateFont', font)
  }

  function toggleZoom() {
    if (!zoomed) {
      zoomed = true
      $previews_are_zoomed = true
      $previews_last_scroll_position = window.scrollY
      hovering = false
    }
    else {
      $previews_are_zoomed = false
      window.setTimeout(() => {
        window.scrollTo(0, $previews_last_scroll_position);
        zoomed = false
      }, 1)      
    }
    dispatch('updateFont', font)
  }
  function handleNameChange() {
    const preferred_name = font.storefronts.find(storefront => storefront.id == $previews_storefronts_filter).preferred_name || font.display_name
    const new_name = prompt(`Enter a new name for ${font.display_name}. \nLeave blank to use the original name.`, preferred_name)
    // Update even if new_name is empty string (allows resetting to original name)
    if (new_name !== null) {
      // If name is blank, set preferred_name to null to use original name
      const storefront = font.storefronts.find(storefront => storefront.id == $previews_storefronts_filter)
      if (new_name === "") {
        storefront.preferred_name = font.display_name
      } else {
        storefront.preferred_name = new_name
      }
      dispatch('updateFont', font)
    }
  }

</script>

{#if !fontinactive}
  <div
    bind:this={preview_element}
    style="font-size:{Math.pow(1.2, $previews_font_size)}em;"
    class="preview"
    class:hidden = {zoomed_but_not_me}
    class:zoomed
    class:selectable = {!$previews_nav_filters.selected && !zoomed && !font.selected && (visits.first() || visits.second())}
    class:zoomable = {$previews_nav_filters.selected && !zoomed}
    class:selected = {font.selected && !$previews_nav_filters.selected && !$previews_nav_filters.storefront}
    class:dense = {$previews_font_size < 2}
    class:paragraph_mode = {$preview_mode === 'paragraphs'}
    on:click|self="{handleClick}"
    on:mouseenter={() => hovering = true}
    on:mouseleave={() => hovering = false}
  >
    {#if hovering && !zoomed}
      {#if $previews_nav_filters.storefront}
        <PreviewButton 
          on:click="{removeFromStorefront}" 
          icon={"icon-times"} 
          position={"right"}
          label={$_('buttons.remove')}/>
        <PreviewButton 
          on:click="{handleNameChange}" 
          icon={"icon-rename"} 
          position={"left"}
          label={$_('buttons.changePreferredName')}/>
      {:else if $previews_nav_filters.selected || font.selected}
        <PreviewButton 
          on:click="{deselect}" 
          icon={"icon-times"} 
          position={"right"}
          label={$previews_nav_filters.selected ? $_('buttons.remove') : $_('buttons.deselect')}/>
      {:else}
        <PreviewButton 
          on:click="{select}" 
          icon={"icon-plus"}
          position={"right"}
          label={$_('buttons.select')}/>
      {/if}
      {#if !$previews_nav_filters.storefront}
        <PreviewButton 
          on:click="{toggleZoom}" 
          icon={"icon-magnify"} 
          position={"left"}
          label={$_('buttons.zoom')}/>
      {/if}
    {/if}
    <!-- Only the first 20 uploaded fonts' font files are loaded -->
    {#if $can_upgrade_from_pro_to_business && font.uploaded && !font.font_file_loaded && !font.font_file_loading  && font.source === "uploaded"}
      <div class="uploadLimitReached">
        <p>Upgrade to preview</p>
      </div>
    {:else}
      <PreviewText {font} {zoomed} on:handleClick="{handleClick}" on:updateFont on:fontinactive={() => fontinactive = true}/>
    {/if}
    <PreviewNameOrDownloadLink {font} hovering_parent={hovering} zoomed_parent={zoomed}/>
    {#if !$previews_nav_filters.storefront}
      <PreviewTags tag_ids={font.tags} hovering_parent={hovering} zoomed_parent={zoomed} font_id={font.id}/>
    {/if}
  </div>
  {#if zoomed}
    <div class="floatingCloseButton">
      <Button kind="transparent" size="small" on:click={toggleZoom}>
        {$_('buttons.close')}
        <svg class="icon"><use xlink:href="#icon-times"/></svg>
      </Button>
    </div>
    <GlyphsViewer font_obj={font}/>
  {/if}
{/if}

<style>
  .preview {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    overflow: hidden;
    padding: 1.5em;
    margin: 1rem;
    font-size: 1.1rem;  /* Base for font size multiplier */
    text-align: center;
    color: var(--c-text);
    background-color: var(--c-content-bg);
    border: 3px solid transparent;
    transition-property: border-color, background-color;
		transition-duration: var(--transition-duration);
		transition-timing-function: var(--transition-function);
  }
  .preview.hidden {
    display: none;
  }
  .preview.selected {
    border: 3px solid var(--c-accent);
  }
  .preview.selectable {
    cursor: cell;
  }
  .preview.zoomed {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: 0 !important;
    padding: 0;
    border-width: 2rem;
    border-top-width: 6rem;
    border-style: solid;
    border-color: var(--c-bg);
    z-index: 4;
  }
  .preview.zoomable {
    cursor: zoom-in;
  }
  .preview.paragraph_mode {
    justify-content: left;
    padding-left: 4rem;
  }
  .preview.zoomed.paragraph_mode {
    justify-content: center;
  }

  .preview.dense {
    margin: 0.5rem;
  }

  .floatingCloseButton {
    position: fixed;
    z-index: 5;
    top: 1rem;
    right: -0.5rem;
  }

  .floatingCloseButton svg {
    vertical-align: middle;
    margin-left: -0.5rem;
  }

  .uploadLimitReached {
    color: var(--c-text-faded);
    font-size: 0.9em;
  }
  
  :global(.preview.print_clone) {
    margin: 1rem !important;
    color: var(--c-text-active) !important;
    background-color: #FFFFFF;
    border: 2px solid var(--c-bg) !important;
  }
  :global(.preview.dense.print_clone) {
    margin: 0.5rem !important;
  }

  :global(.preview.zoomed.print_clone) {
    position: absolute !important;
    z-index: unset;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    left: 0 !important;
    margin: 0 auto !important;

    color: var(--c-text-active) !important;
    background-color: #FFFFFF;
    border: 2rem solid var(--c-bg) !important;
  }
  :global(.preview.zoomed.fontname) {
    bottom: 2rem !important;
  }

  :global(.preview.zoomed.preview_clone) {
    position: relative !important;
    z-index: unset;
    top: unset !important;
    right: unset !important;
    bottom: unset !important;
    left: unset !important;
    margin: 0 auto !important;
  }

</style>
